// dynamic_map_scene.js - 动态地图场景类
// 根据JSON配置自动创建场景，无需为每个地图单独创建JS文件

import BaseWorldScene from './base_world_scene.js';
import DebugHelper from './modules/DebugHelper.js';
import GameStateStore from './modules/GameStateStore.js';
import { isCave, SCENE_BASE_CLASS } from './modules/SceneClassHelper.js';

/**
 * 场景工厂函数
 * 根据JSON配置创建场景实例
 * @param {string} sceneKey - 场景key（用于Phaser识别）
 * @param {string} mapKey - 地图JSON的key（用于从缓存读取数据）
 * @returns {Function} 场景类构造函数
 */
export function createMapScene(sceneKey, mapKey) {
    // 返回一个场景类
    class MapScene extends BaseWorldScene {
        constructor() {
            // 先调用父类构造函数，传入默认配置
            // 实际配置会在init中从JSON读取
            super(sceneKey, mapKey, {});
            
            // 保存场景key和mapKey
            this._sceneKey = sceneKey;
            this._mapKey = mapKey;
        }
        
        /**
         * 初始化场景
         * 在这里读取JSON配置并应用
         */
        init(data = {}) {
            // 先获取地图JSON数据
            const mapData = this.cache.json.get(this._mapKey);
            if (!mapData) {
                DebugHelper.error(`DynamicMapScene: 无法加载地图数据 ${this._mapKey}`);
                return;
            }
            
            // 读取场景配置（如果JSON中有sceneConfig字段）
            const sceneConfig = mapData.sceneConfig || {};
            
            // 更新config（在调用super.init之前）
            this.config = {
                enableRandomEncounter: sceneConfig.enableRandomEncounter !== false,
                enableResumeEvent: sceneConfig.enableResumeEvent !== false,
                welcomeMessage: sceneConfig.welcomeMessage || '',
                bgmKey: sceneConfig.bgmKey || null,
                baseClass: sceneConfig.baseClass || SCENE_BASE_CLASS.WORLD,
                ...sceneConfig
            };
            
            // 如果是WorldScene，需要特殊处理玩家位置
            // WorldScene优先使用gameState中的坐标，而不是传入的spawn坐标
            if (this._sceneKey === 'WorldScene' && !data.spawnX && !data.spawnY) {
                const player = GameStateStore.getPlayer();
                
                // 世界地图优先使用gameState中的坐标
                if (player) {
                    data.spawnX = player.tileX;
                    data.spawnY = player.tileY;
                }
            }
            
            // 调用父类的init
            return super.init(data);
        }
        
        /**
         * 重写transitionToScene，如果是洞穴场景则禁用淡入淡出
         */
        transitionToScene(exit, options = {}) {
            if (isCave(this.config)) {
                // 洞穴场景不使用淡入淡出
                super.transitionToScene(exit, { ...options, useFade: false });
            } else {
                super.transitionToScene(exit, options);
            }
        }
        
        /**
         * 场景创建完成后的回调
         */
        onSceneCreated() {
            const mapData = this.cache.json.get(this._mapKey);
            const mapName = mapData?.name || this._mapKey;
            DebugHelper.log(`场景 ${this._sceneKey} (${mapName}) 创建完成`);
        }
    }
    
    return MapScene;
}

